import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './DeleteAdModal.styles';
import { RemoveAdPayload } from '@/components/Ad/Settings/Settings.types';
import { DeleteReason } from '@/api/types/ads.types';
import { useResponsive } from '@/hooks/useResponsive';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { sleep } from '@/utils/helpers';
import { MODAL_ANIMATION_DURATION } from '@/styles/constants';
import { Textarea } from '@/components/UI/Textarea/Textarea';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { Popup } from '@/components/UI/Popup/Popup';
import { ModalSheet } from '@/components/UI/ModalSheet';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';

const DESCRIPTION_LIMIT = 500;

type DeleteAdModalProps = {
  isOpen: boolean;
  toggle: () => void;
  submit: (payload: RemoveAdPayload) => void;
  reasons: DeleteReason[];
};

export function DeleteAdModal({
  isOpen,
  toggle,
  submit,
  reasons = [],
}: DeleteAdModalProps) {
  const [isConfirmView, setConfirmView] = useState(false);
  const [selected, setSelected] = useState<DeleteReason | null>(null);
  const [description, setDescription] = useState('');
  const [isDescriptionView, setIsDescriptionView] = useState(false);
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { isDesktop } = useResponsive();
  useBodyOverflow(isOpen || isConfirmView);

  const handleClose = useCallback(() => {
    setConfirmView(false);
    toggle();
    sleep(MODAL_ANIMATION_DURATION).then(() => {
      setSelected(null);
      setDescription('');
      setConfirmView(false);
      setIsDescriptionView(false);
    });
  }, [toggle]);

  const handleSubmit = () => {
    if (selected) {
      submit({
        id: selected.id,
        name: selected.name,
        delete_reason_description: description,
      });
    }
  };

  const handleDeleteClick = () => {
    if (!selected) return;
    if (selected.descriptionRequired) {
      setIsDescriptionView(true);
    } else {
      setDescription('');
      setConfirmView(true);
    }
  };

  const renderDescriptionView = () => (
    <>
      <S.DeleteAdModalDescriptionHead>
        <S.DeleteAdModalDescriptionHeadButton
          onClick={() => {
            setIsDescriptionView(false);
            setDescription('');
          }}
        >
          <S.DeleteAdModalDescriptionBackIcon />
        </S.DeleteAdModalDescriptionHeadButton>
        <S.DeleteAdModalDescriptionTitle>
          {t('pages.ad.expire_reason')}
        </S.DeleteAdModalDescriptionTitle>
        <S.DeleteAdModalDescriptionHeadButton onClick={handleClose}>
          <S.DeleteAdModalDescriptionCloseIcon />
        </S.DeleteAdModalDescriptionHeadButton>
      </S.DeleteAdModalDescriptionHead>
      <Textarea
        data-testid={AdPageTestIds.remove.descriptionInput}
        ref={textareaRef}
        appearance="quartenary"
        autoFocus
        onBlur={() => textareaRef.current?.focus()}
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder={t('pages.ad.expire_reason_placeholder')}
        height={125}
        withSymbolsCount
        maxSymbolsCount={DESCRIPTION_LIMIT}
        symbolsCountTextPlacement="bottom"
        symbolsCountFontSize={isDesktop ? 14 : 12}
      />
      <S.DeleteAdModalButton
        data-testid={AdPageTestIds.remove.continueButton}
        appearance="primary"
        onClick={() => setConfirmView(true)}
        $hasMargin
        $isDescriptionView
      >
        {t('pages.ad.expire_continue')}
      </S.DeleteAdModalButton>
    </>
  );

  if (isConfirmView) {
    return (
      <Popup
        isOpen={isConfirmView}
        close={handleClose}
        isInPortal
        variant="1"
        size="xlarge"
      >
        <S.DeleteAdModalPopupContent>
          <S.DeleteAdModalPopupTitle>
            {t('pages.ad.expire_confirm')}
          </S.DeleteAdModalPopupTitle>
          <S.DeleteAdModalPopupDescription>
            {t('pages.ad.expire_confirm_body')}
          </S.DeleteAdModalPopupDescription>
        </S.DeleteAdModalPopupContent>
        <S.DeleteAdModalPopupActions>
          <S.DeleteAdModalButton
            data-testid={AdPageTestIds.remove.submitButton}
            appearance="redOutlined"
            onClick={() => {
              handleSubmit();
              handleClose();
            }}
          >
            {t('common.remove')}
          </S.DeleteAdModalButton>
          <S.DeleteAdModalButton appearance="secondary" onClick={handleClose}>
            {t('common.decline')}
          </S.DeleteAdModalButton>
        </S.DeleteAdModalPopupActions>
      </Popup>
    );
  }

  return (
    <ModalSheet
      dataTestId={AdPageTestIds.remove.sheet}
      isOpen={isOpen}
      onClose={handleClose}
      title={!isDescriptionView ? t('pages.ad.expire') : ''}
    >
      {isDescriptionView ? (
        renderDescriptionView()
      ) : (
        <>
          {reasons.map(reason => (
            <S.DeleteAdModalListItem
              key={reason.id}
              data-testid={`remove-reason-${reason.id}`}
            >
              <Checkbox
                size="md"
                value={String(reason.id) === String(selected?.id)}
                label={reason.name}
                appearance="radio"
                color="secondary"
                onChange={e =>
                  setSelected(e.currentTarget.checked ? reason : null)
                }
              />
            </S.DeleteAdModalListItem>
          ))}
          <S.DeleteAdModalButton
            data-testid={AdPageTestIds.remove.reasonsNextButton}
            appearance="red"
            disabled={!selected}
            onClick={handleDeleteClick}
            $hasMargin
          >
            {t('pages.ad.expire_submit')}
          </S.DeleteAdModalButton>
        </>
      )}
    </ModalSheet>
  );
}
